// Quick guide — Irregular verbs (present tense), grouped by the pattern of their
// irregularity. Most "irregular" verbs aren't random — they fall into a few
// families. Each family now shows a FULL model conjugation so the learner sees
// the whole shape, not just the odd form. Hand-drawn paper style with audio.
import { speakButtonHTML } from "../lib/audio.js";

const PRONOUNS = ["eu", "tu", "ele / ela", "nós", "vós", "eles / elas"];

// A full 6-form conjugation table. `hi` is the set of row indices to highlight
// (the irregular / "boot" forms); highlighted rows get the rose treatment.
function conjTable(forms, hi) {
  const set = new Set(hi || []);
  const rows = forms
    .map((form, i) => {
      const on = set.has(i);
      return `
        <div class="flex items-baseline gap-2 py-0.5 px-1.5 rounded ${on ? "bg-rose-50" : ""}">
          <span class="w-24 shrink-0 text-sage-500 text-base" style="font-family:Inter,sans-serif">${PRONOUNS[i]}</span>
          <span class="font-bold ${on ? "text-rose-700" : "text-sage-800"}">${form}</span>${speakButtonHTML(form)}
        </div>`;
    })
    .join("");
  return `<div class="mt-1">${rows}</div>`;
}

// A family card: title, note, the model verb's infinitive, and its full table.
function familyCard({ color, title, note, inf, gloss, forms, hi, extra }) {
  return `
    <div class="sketch-box-sm bg-white/30 p-4 mb-4 break-inside-avoid">
      <h3 class="${color} text-2xl sketch-underline inline-block">${title}</h3>
      <p class="text-sm text-sage-500 mb-2 mt-1" style="font-family:Inter,sans-serif">${note}</p>
      <p class="text-lg text-sage-700">Model: <b class="text-sage-800">${inf}</b> <span class="text-base text-sage-500" style="font-family:Inter,sans-serif">— ${gloss}</span>${speakButtonHTML(inf)}</p>
      ${conjTable(forms, hi)}
      ${extra || ""}
    </div>`;
}

// Boot families — one model verb each, boot forms highlighted. The boot is
// eu · tu · ele · eles (everything except nós / vós = rows 0,1,2,5). The -ear /
// -iar verbs change across the whole boot; the -ir raisers change only in eu.
const BOOT = [0, 1, 2, 5];

const STEM_FAMILIES = [
  {
    color: "text-emerald-700",
    title: "e → i (eu only)",
    note: "Many -ir verbs raise the stem e to i, but only in the eu form.",
    inf: "servir",
    gloss: "to serve",
    forms: ["sirvo", "serves", "serve", "servimos", "servis", "servem"],
    hi: [0],
  },
  {
    color: "text-emerald-700",
    title: "o → u (eu only)",
    note: "Some -ir verbs raise the stem o to u, again only in the eu form.",
    inf: "dormir",
    gloss: "to sleep",
    forms: ["durmo", "dormes", "dorme", "dormimos", "dormis", "dormem"],
    hi: [0],
  },
  {
    color: "text-emerald-700",
    title: "-ear → ei",
    note: "Verbs in -ear add an i (ei) across the whole boot — every form except nós / vós.",
    inf: "passear",
    gloss: "to stroll",
    forms: ["passeio", "passeias", "passeia", "passeamos", "passeais", "passeiam"],
    hi: BOOT,
  },
  {
    color: "text-emerald-700",
    title: "-iar (odiar group)",
    note: "A handful of -iar verbs (odiar, ansiar…) also take ei in the boot.",
    inf: "odiar",
    gloss: "to hate",
    forms: ["odeio", "odeias", "odeia", "odiamos", "odiais", "odeiam"],
    hi: BOOT,
  },
];

const OTHER_FAMILIES = [
  {
    color: "text-amber-700",
    title: "“Eu” irregulars (-ço / -go)",
    note: "Only the eu form is odd — the rest are regular.",
    inf: "fazer",
    gloss: "to do / make",
    forms: ["faço", "fazes", "faz", "fazemos", "fazeis", "fazem"],
    hi: [0],
    extra: `<p class="text-sm text-sage-500 mt-2" style="font-family:Inter,sans-serif">Same odd eu form: <b>dizer</b> → digo · <b>trazer</b> → trago · <b>ouvir</b> → ouço · <b>pedir</b> → peço.</p>`,
  },
  {
    color: "text-violet-700",
    title: "Nasal eu form (-nho)",
    note: "A few verbs take a nasal -nho in the eu form and stay irregular elsewhere.",
    inf: "ter",
    gloss: "to have",
    forms: ["tenho", "tens", "tem", "temos", "tendes", "têm"],
    hi: [0, 2, 5],
    extra: `<p class="text-sm text-sage-500 mt-2" style="font-family:Inter,sans-serif">eu = <b>tenho</b>; note <b>ele tem</b> and <b>eles têm</b> (circumflex). <b>vir</b> works the same → venho, vens, vem… eles vêm.</p>`,
  },
  {
    color: "text-sky-700",
    title: "Spelling change (c → ç)",
    note: "Verbs ending -cer change the c to ç before o to keep the soft sound in the eu form.",
    inf: "conhecer",
    gloss: "to know (a person/place)",
    forms: ["conheço", "conheces", "conhece", "conhecemos", "conheceis", "conhecem"],
    hi: [0],
    extra: `<p class="text-sm text-sage-500 mt-2" style="font-family:Inter,sans-serif">Same family: <b>parecer</b> → pareço · <b>esquecer</b> → esqueço · <b>aquecer</b> → aqueço.</p>`,
  },
  {
    color: "text-rose-600",
    title: "Totally irregular — ser",
    note: "No pattern — learn the whole shape by heart.",
    inf: "ser",
    gloss: "to be",
    forms: ["sou", "és", "é", "somos", "sois", "são"],
    hi: [0, 1, 2, 3, 4, 5],
  },
  {
    color: "text-rose-600",
    title: "Totally irregular — ir",
    note: "Also fully irregular — and note it looks nothing like “ir”.",
    inf: "ir",
    gloss: "to go",
    forms: ["vou", "vais", "vai", "vamos", "ides", "vão"],
    hi: [0, 1, 2, 3, 4, 5],
  },
];

export function irregularVerbsView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Irregular Verbs ⚡</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Present tense. Most “irregular” verbs aren't random — they belong to a few families. Learn the family and one model verb in full, and the rest fall into line. Rose-tinted rows are the forms that break from the regular pattern.</p>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg mb-6">
        <h3 class="text-2xl text-emerald-700 sketch-underline inline-block">1 · Stem-changers (the “boot” 👢)</h3>
        <p class="text-sage-700 mt-1 mb-3">Some verbs change only in the <b>eu</b> form; the <b>-ear</b> and <b>-iar</b> verbs change across the whole boot — every form <b>except nós / vós</b>. Shade in the changed forms and they make a boot shape.</p>
        <div class="columns-1 sm:columns-2 gap-4">
          ${STEM_FAMILIES.map(familyCard).join("")}
        </div>
      </div>

      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        <h3 class="text-2xl text-amber-700 sketch-underline inline-block">2 · The other families</h3>
        <p class="text-sage-700 mt-1 mb-3">These break in the <b>eu</b> form, or — for ser and ir — everywhere.</p>
        <div class="columns-1 sm:columns-2 gap-4">
          ${OTHER_FAMILIES.map(familyCard).join("")}
        </div>
      </div>

      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">ser · estar · ter · ir get their own deep-dive in <b>The Big 4 Verbs</b>. For any verb's full conjugation across all tenses, use the Verb Index.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
